import {
  children,
  createEffect,
  createMemo,
  JSX,
  Match,
  ParentComponent,
  Show,
  Switch,
} from 'solid-js';
import { setClassAttribute, setDisabledAttribute } from '@trimble/utilities';
import { Badge } from './ProBadge';
import { TieringBehaviors, TieringFeatures } from '@trimble/types';
import { useTiering } from './tiering-provider/TieringProvider';
import { t } from '@trimble/i18n';

export type TierLockProps = {
  children: JSX.Element;
  preserveWidth?: boolean;
  targetFeature: TieringFeatures;
  options: {
    showBadge: boolean;
    disabledBehavior: TieringBehaviors;
    isDisabledDefault?: boolean;
  };
};

/**
 * Use this component to wrap any other components that need to be restricted to
 * specific licensing tiers. It will apply the configured behavior if the
 * user lacks the correct feature permission.
 *
 * For the 'Restricted' behavior it will apply the disabled attribute to all
 * child elements of this one
 */
export function TierLock(props: TierLockProps): JSX.Element {
  let ref!: HTMLDivElement;

  const { tierFeatures } = useTiering();
  const safeChildren = children(() => props.children);
  const currentOptions = createMemo(() => props.options);
  const isActive = createMemo(() =>
    tierFeatures().includes(props.targetFeature)
  );

  const TOOLTIP_MESSAGE = t('general.tiering.tooltip');

  /**
   * Applies the disabled attribute to every child element of this
   * element if it is restricted.
   *
   * ToDo: This is a particularly naive and aggressive way of disabling
   *  elements and should be refactored to avoid accessing the DOM and
   *  recursing through all the child component if possible.
   */
  createEffect(() => {
    if (
      !isActive() &&
      currentOptions().disabledBehavior === TieringBehaviors.Restricted
    ) {
      setDisabledAttribute(ref);
      setClassAttribute(ref, 'opacity-90');
    } else {
      if (!props.options.isDisabledDefault) {
        setDisabledAttribute(ref, 'remove');
        setClassAttribute(ref, 'opacity-90', 'remove');
      }
    }
  });

  return (
    <Switch>
      <Match
        when={
          isActive() ||
          currentOptions().disabledBehavior === TieringBehaviors.Unrestricted
        }
      >
        <div ref={ref}>{safeChildren()}</div>
      </Match>
      <Match
        when={currentOptions().disabledBehavior === TieringBehaviors.Restricted}
      >
        <modus-wc-tooltip content={TOOLTIP_MESSAGE}>
          <RestrictedElement
            preserveWidth={props.preserveWidth}
            showBadge={props.options.showBadge}
            ref={ref}
          >
            {props.children}
          </RestrictedElement>
        </modus-wc-tooltip>
      </Match>
      <Match
        when={currentOptions().disabledBehavior === TieringBehaviors.Hidden}
      >
        <div ref={ref} />
      </Match>
    </Switch>
  );
}

interface RestrictedElementProps {
  children: JSX.Element;
  showBadge: boolean;
  preserveWidth?: boolean;
  ref: HTMLDivElement;
}

const RestrictedElement: ParentComponent<RestrictedElementProps> = (props) => {
  const safeChildren = children(() => props.children);
  return (
    <Show
      when={props.showBadge}
      fallback={<div ref={props.ref}>{safeChildren()}</div>}
    >
      <Badge preserveWidth={props.preserveWidth} ref={props.ref!}>
        {safeChildren()}
      </Badge>
    </Show>
  );
};
